'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Image, Text, useResizeObserver } from '@weareconceptstudio/core';
import { ProductItem } from '@weareconceptstudio/ecommerce/v1';
import Button from '@/components/Button';
import { config } from '@/helpers';
import MostWantedStyle from './style';

gsap.registerPlugin(ScrollTrigger);

const MostWanted = ({ data, mostWantedData: rawMostWantedData }) => {
	// The API can return a non-array (e.g. empty response); never call .map on it.
	const mostWantedData = Array.isArray(rawMostWantedData) ? rawMostWantedData : [];

	//! Refs
	const wrapperRef = useRef();
	const leftBarRef = useRef();
	const rightBarRef = useRef();
	const innerRightBarRef = useRef();
	const innerWrapperLeftBar = useRef();
	const contentRightRef = useRef();
	const contentWrapperRef = useRef();

	//! Resizes
	const resizes = useResizeObserver(typeof window !== 'undefined' ? document.body : null);

	//! calculate left-right bar height
	useEffect(() => {
		if (typeof window === 'undefined') return; // SSR safety

		const matchMedia = gsap.matchMedia();

		if (
			rightBarRef.current &&
			wrapperRef.current &&
			mostWantedData.length > 0 &&
			leftBarRef.current &&
			innerRightBarRef.current &&
			innerWrapperLeftBar.current
		) {
			matchMedia.add('(min-width: 1280px)', () => {
				// Get gap variable safely
				const variableValueGap = getComputedStyle(wrapperRef.current).getPropertyValue('--gapDistance').trim();

				// First product height
				const itemHeight = innerRightBarRef.current.children[0]?.clientHeight || 0;

				// Set heights
				if (leftBarRef.current) {
					leftBarRef.current.style.height = `${rightBarRef.current.clientHeight - 2}px`;
				}
				if (innerRightBarRef.current) {
					innerRightBarRef.current.style.height = `calc(${itemHeight * 2}px + ${variableValueGap})`;
				}
				if (innerWrapperLeftBar.current) {
					innerWrapperLeftBar.current.style.height = `calc(${itemHeight * 2}px + ${variableValueGap})`;
				}

				const headerHeight = document.querySelector('header')?.clientHeight || 0;

				// Left bar pin
				ScrollTrigger.create({
					start: `top-=${headerHeight} top`,
					end: `bottom-=${(innerWrapperLeftBar.current?.clientHeight || 0) + headerHeight}px top`,
					trigger: contentWrapperRef.current,
					pin: leftBarRef.current,
					pinSpacing: false,
					id: 'left-bar',
				});

				// Right bar pin + animation
				ScrollTrigger.create({
					start: `top-=${headerHeight} top`,
					end: `bottom-=${(innerWrapperLeftBar.current?.clientHeight || 0) + headerHeight}px top`,
					trigger: contentWrapperRef.current,
					pin: rightBarRef.current,
					pinSpacing: false,
					id: 'right-bar',
					onUpdate: (self) => {
						const progress = self.progress;
						if (innerRightBarRef.current && leftBarRef.current && innerWrapperLeftBar.current) {
							gsap.to(innerRightBarRef.current, {
								duration: 0,
								y:
									(leftBarRef.current.clientHeight - innerWrapperLeftBar.current.clientHeight) *
									-progress,
							});
						}
					},
				});
			});
		}

		// Cleanup
		return () => {
			const left = ScrollTrigger.getById('left-bar');
			const right = ScrollTrigger.getById('right-bar');

			if (left) left.kill();
			if (right) right.kill();

			matchMedia.revert();

			if (leftBarRef.current) leftBarRef.current.style.height = `auto`;
			if (innerRightBarRef.current) innerRightBarRef.current.style.height = `auto`;
			if (innerWrapperLeftBar.current) innerWrapperLeftBar.current.style.height = `auto`;
		};
	}, [resizes, rawMostWantedData]);

	return (
		<MostWantedStyle
			ref={wrapperRef}
			className='most-wanted-wrapper'>
			<Container isSection>
				<div className='title-section'>
					<Text
						className='uppercase h2'
						tag='h2'
						text='mostWanted'
					/>
				</div>

				<div
					ref={contentWrapperRef}
					className='inner-wrapper'>
					{/* Left Bar */}
					<div
						ref={leftBarRef}
						className='left-bar'>
						<div
							ref={innerWrapperLeftBar}
							className='inner-wrapper-left-bar'>
							<div className='wrapper-image'>
								<Image
									src={data.image.src}
									alt={data.image.alt}
								/>
							</div>
							<div className='wrapper-description-button'>
								<div className='inner-wrapper-description'>
									<div className='description'>
										<Text className='uppercase h4'>{data.title}</Text>
									</div>
									<Button
										text='orderNow'
										className='wrapper-button'
										url={config.routes.menu.path}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Right Bar */}
					<div
						ref={rightBarRef}
						className='right-bar'>
						<div
							ref={contentRightRef}
							className='inner-wrapper-right-bar'>
							<div
								ref={innerRightBarRef}
								className='content-right-bar'>
								{mostWantedData.map((product, i) => (
									<ProductItem
										key={product.id}
										className={`item-product ${Math.floor(mostWantedData.length / 3) * 3 < i + 1 ? `ended-child-${mostWantedData.length - Math.floor(mostWantedData.length / 3) * 3}` : ''}`}
										{...product}
										image={product.thumbnail || product.image}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			</Container>
		</MostWantedStyle>
	);
};

export default MostWanted;
